import { MapPin, Plus, Search, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { getSeries, searchTerritories } from '../api/endpoints';
import { useFilters } from '../app/FiltersContext';
import TerritoryComparisonChart from '../components/charts/TerritoryComparisonChart';
import ErrorState from '../components/feedback/ErrorState';
import LoadingState from '../components/feedback/LoadingState';
import QuickFuelControls from '../components/filters/QuickFuelControls';
import PublicPageHeader from '../components/public/PublicPageHeader';
import SectionLabel from '../components/public/SectionLabel';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';

export default function ComparePage() {
  const { fuel, service, from, to } = useFilters();
  const [territories, setTerritories] = useState([{ type: 'country', code: 'IT', name: 'Italia' }]);
  const [metric, setMetric] = useState('average_price');
  const [q, setQ] = useState('');
  const [options, setOptions] = useState([]);
  const [state, setState] = useState({ loading: false, error: null, series: [] });

  useEffect(() => {
    const t = setTimeout(() => q.trim().length >= 2 ? searchTerritories(q).then(setOptions).catch(() => setOptions([])) : setOptions([]), 220);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    let active = true;
    setState(s => ({ ...s, loading: true, error: null }));
    Promise.all(
      territories.map(t => getSeries({ fuel, service, from, to, geo_level: t.type, geo_code: t.code }).then(d => ({ territory: t, items: d.items || [] })))
    ).then(series => active && setState({ loading: false, error: null, series })).catch(error => active && setState({ loading: false, error, series: [] }));
    return () => { active = false; };
  }, [territories, fuel, service, from, to]);

  const add = o => {
    if (territories.length < 5 && !territories.some(t => t.type === o.type && t.code === o.code)) setTerritories([...territories, o]);
    setQ('');
    setOptions([]);
  };

  return (
    <div>
      <PublicPageHeader
        tone="secondary"
        title="Lo stesso carburante, territori diversi."
        description="Metti Italia, regioni, province e comuni sullo stesso asse temporale. Ogni punto resta una giornata reale del dataset."
      />

      <div className="mx-auto max-w-5xl px-4 pb-12 pt-7 sm:px-6 sm:pt-10">
        <div className="grid min-w-0 gap-5 lg:grid-cols-[360px_minmax(0,1fr)]">
          <aside className="min-w-0 rounded-[36px_16px_36px_36px] bg-md-surface-container p-5 sm:p-6">
            <div className="mb-5">
              <div className="text-lg font-medium text-md-on-surface">Cosa stai confrontando</div>
              <p className="mt-1 text-sm leading-6 text-md-on-surface-variant">Carburante e modalità valgono per tutte le linee.</p>
            </div>
            <QuickFuelControls showTerritory={false} />

            <div className="mt-7 border-t border-md-outline-variant pt-6">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div className="text-sm font-medium text-md-on-surface">Territori</div>
                <span className="rounded-full bg-md-secondary-container px-3 py-1 text-xs font-bold text-md-on-secondary-container">{territories.length}/5</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {territories.map(t => (
                  <div key={`${t.type}-${t.code}`} className="inline-flex min-h-10 items-center gap-2 rounded-full bg-md-primary-container px-4 text-sm font-medium text-md-on-primary-container">
                    {t.name}
                    {!(t.type === 'country' && t.code === 'IT') && (
                      <button aria-label={`Rimuovi ${t.name}`} className="grid h-6 w-6 place-items-center rounded-full hover:bg-md-surface" onClick={() => setTerritories(territories.filter(x => x !== t))}><X className="h-3.5 w-3.5" /></button>
                    )}
                  </div>
                ))}
              </div>

              {territories.length < 5 && (
                <div className="relative mt-4">
                  <Search className="pointer-events-none absolute left-4 top-[19px] z-10 h-4 w-4 text-md-on-surface-variant" />
                  <Input className="filled-field pl-11" value={q} onChange={e => setQ(e.target.value)} placeholder="Aggiungi un territorio" />
                  {options.length > 0 && (
                    <div className="absolute z-30 mt-2 w-full rounded-[24px] bg-md-surface-container-low p-2 shadow-md2">
                      {options.slice(0, 8).map(o => (
                        <Button key={`${o.type}-${o.code}`} variant="ghost" className="min-h-11 w-full justify-start px-4 text-md-on-surface hover:bg-md-primary-container hover:text-md-on-primary-container" onClick={() => add(o)}>
                          <Plus className="mr-2 h-4 w-4" /><span className="truncate">{o.name}</span>
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </aside>

          <section className="min-w-0 overflow-hidden rounded-[16px_38px_38px_38px] bg-md-secondary-container p-3 sm:p-6">
            <div className="flex flex-col gap-4 px-2 pb-4 sm:flex-row sm:items-center sm:justify-between sm:px-0">
              <div>
                <SectionLabel tone="neutral" className="bg-md-surface">Confronto giornaliero</SectionLabel>
                <h2 className="mt-3 text-2xl font-medium tracking-[-0.025em] text-md-on-secondary-container">Media o mediana?</h2>
              </div>
              <Tabs value={metric} onValueChange={setMetric}>
                <TabsList className="bg-md-surface">
                  <TabsTrigger value="average_price" className="data-[state=active]:bg-md-secondary data-[state=active]:text-md-on-secondary">Media</TabsTrigger>
                  <TabsTrigger value="median_price" className="data-[state=active]:bg-md-secondary data-[state=active]:text-md-on-secondary">Mediana</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            <div className="min-h-[430px] rounded-[30px_30px_10px_30px] bg-md-surface p-1 sm:p-3">
              {state.loading ? <LoadingState minHeight={430} /> : state.error ? <ErrorState error={state.error} /> : <TerritoryComparisonChart series={state.series} metric={metric} height={430} fuel={fuel} />}
            </div>
            <div className="mt-4 flex items-start gap-2 px-2 text-xs leading-5 text-md-on-secondary-container sm:px-0">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
              La media non è ponderata sui litri venduti. La mediana riduce l’effetto dei valori estremi.
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
